import { ApolloClient, HttpLink, InMemoryCache } from "@apollo/client/core";
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  ServerError,
  ServerParseError,
} from "@apollo/client/errors";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import i18n from "i18next";
import { useAuthStore } from "@/stores/useAuth";
import { clearPremiumGracePeriod } from "@/lib/premiumGracePeriod";
import { resolveApiLocale } from "@/lib/i18n/apiLocale";
import { isShowNotFound } from "@/lib/showNotFound";
import { isLikeOperation, isStaleLikeError } from "@/lib/staleLike";

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_API_BASE_URL,
});

const authLink = new SetContextLink(({ headers }) => {
  const accessToken = useAuthStore.getState().accessToken;
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

// Overrides the browser's own header so the API localizes on the locale the
// user picked in the app, not on the one their browser happens to advertise.
const localeLink = new SetContextLink(({ headers }) => ({
  headers: {
    ...headers,
    "accept-language": resolveApiLocale(i18n.resolvedLanguage),
  },
}));

function sentryGraphqlFormattedError(
  formattedError: { message: string; path?: unknown; extensions?: unknown },
  kind: "graphql" | "protocol",
  operationName: string | undefined,
) {
  const err = new Error(formattedError.message);
  err.name = kind === "graphql" ? "GraphQLError" : "GraphQLProtocolError";
  Sentry.captureException(err, {
    tags: { kind },
    extra: {
      operationName,
      path: formattedError.path,
      extensions: formattedError.extensions,
    },
  });
}

const RELOAD_GUARD_KEY = "auth-401-reloaded-at";
const RELOAD_GUARD_WINDOW_MS = 10_000;

const recoverFromUnauthorized = () => {
  useAuthStore.getState().resetAuthStore();
  clearPremiumGracePeriod();
  Sentry.setUser(null);

  const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - lastReload < RELOAD_GUARD_WINDOW_MS) {
    Sentry.captureMessage("401 recovery loop: skipping reload", "warning");
    return;
  }
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
};

// Exported for `tests/unit/api/errorLink.test.ts` only: what it reports, and
// what it deliberately doesn't, is a contract nothing in the browser can see.
export const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const graphQLError of error.errors) {
      // A dead token 401s every request; reset the session and reload so boot
      // re-registers anonymously. Skip Register itself to avoid a loop.
      if (
        graphQLError.extensions?.code === "UNAUTHENTICATED" &&
        operation.operationName !== "Register"
      ) {
        recoverFromUnauthorized();
        return;
      }
      // A stale show link is a 404 the player renders (or a deeplink sends
      // home), not an incident. Nothing else queries a show by id before the
      // player has it, so a link the app itself rendered to a dead show goes
      // through this same operation: the player's loader reports that one on
      // its own, keyed on the navigation being in-app.
      if (operation.operationName === "GetShow" && isShowNotFound(graphQLError))
        continue;
      // A like toggle sent against a state the server doesn't hold: the hook
      // reads the state back, nothing to look at here (SHO-824).
      if (
        isLikeOperation(operation.operationName) &&
        isStaleLikeError(graphQLError)
      )
        continue;
      sentryGraphqlFormattedError(
        graphQLError,
        "graphql",
        operation.operationName,
      );
    }
  } else if (CombinedProtocolErrors.is(error)) {
    for (const protocolError of error.errors) {
      sentryGraphqlFormattedError(
        protocolError,
        "protocol",
        operation.operationName,
      );
    }
  } else if (ServerError.is(error) || ServerParseError.is(error)) {
    // The server answered, but not with GraphQL — a 5xx page, a gateway
    // timeout, a body that isn't JSON. That is the API being broken.
    Sentry.captureException(error, {
      tags: { kind: "network" },
      extra: { operationName: operation.operationName },
    });
  }
  // Anything else is the transport itself failing — offline, DNS, a dropped
  // connection: one opaque `TypeError` per in-flight request, and nobody acts
  // on it. The call sites that care (payment, player, home rails) report on
  // their own terms, so reporting it here only multiplied the same outage.
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(localeLink).concat(httpLink),
  cache: new InMemoryCache({
    // `ImageSizes` has no id, so Apollo doesn't normalize it and, by default,
    // *replaces* the object on `Show:{id}` whenever a query writes it. Two
    // queries select a show's images differently — the catalog fragment
    // reads `displayThumbnail { original medium }`, `GetShow` `{ original }`
    // (SHO-766) — so the player's intent preload (a hover) dropped `medium`,
    // the home's cached layout became incomplete and its cache-and-network
    // query refetched the layout and every section on every hover (SHO-778).
    // Merging field by field keeps every selection whole, whichever writes.
    typePolicies: { ImageSizes: { merge: true } },
  }),
  clientAwareness: {
    name: "ShortsWebClient",
    version: __APP_VERSION__,
  },
});
